import { useCallback } from "react";

import * as Sentry from "@sentry/react";
import {
    AbfGeneratorResult,
    AbfMultiPartTransactionSenderParams,
    AbfTransactionDetails,
    useSendTransactions,
    TransactionSenderOptions,
    TransactionMessageOverrides,
    BaseAbfMultiPartTransactionSender,
    TransactionWalletAuth,
    useSignInTransactionAuth,
    AlerterOptions,
    TRANSACTION_SETTINGS_COOKIE_PREFIX,
    getSlice,
    getDebugWallet,
    useSignAndSendTransactions,
    useUpdateVaultMetadataWithSignatureMutation,
    type UpdateVaultMetadataParams,
    ToggleLoanPositionParams,
    useToggleLoanPositionMutation,
    type SetRefinanceConstraintParams,
    useSetRefinanceConstraintMutation
} from "@bridgesplit/abf-react";
import {
    OrderedTransactions,
    simulateTransaction,
    TransactionResult,
    useAlert,
    useLocalStorage,
    useTransactionsState,
    trySerializeAndEncodeTransaction,
    isVersionedTransaction
} from "@bridgesplit/react";
import {
    Result,
    TransactionStatus,
    getReadableErrorMessage,
    generateNonce,
    parseErrorFromOptions,
    TX_FAILED_TO_CONFIRM,
    RPC_URL,
    TransactionSettingsHeader,
    SOL_DECIMALS,
    uiAmountToLamports,
    CustomContractErrors,
    logTransactionDebugInfo,
    simulateBundle,
    base64,
    getUnixTs,
    UNKNOWN_ERROR
} from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { TrackTransactionData, TrackTransactionEvent } from "app/types";
import { useTrack } from "app/hooks";
import { Connection, VersionedTransaction } from "@solana/web3.js";
import { AppDialog, useAppDialog } from "app/utils";
import { useSelector } from "react-redux";

import { ResolveAnyWalletParams } from "./type";
import { getStaleOracleSimulationMessageFromTransactionResult } from "./simulationErrors";

/**
 * Signs a message with the wallet to prove ownership and sends the POST with signature in the payload.
 * Backend verifies the signature and performs the update. No transaction from backend - uses wallet.signMessage.
 * Uses useResolveAnyWalletErrors for auth; cancellation is handled there and surfaces as "Transaction cancelled" toast.
 */

const UPDATE_VAULT_METADATA_MESSAGE_PREFIX = "Verifying wallet address for vault metadata update";
const TOGGLE_LOAN_POSITION_MESSAGE_PREFIX = "Verifying wallet address for loan position update";
// Must match the backend verbatim (markets `build_set_min_liquidation_threshold_message`).
const SET_REFINANCE_CONSTRAINT_MESSAGE_PREFIX = "Set refinance liquidation threshold floor for loan";

function getUpdateVaultMetadataMessage(timestamp: number): string {
    return `${UPDATE_VAULT_METADATA_MESSAGE_PREFIX}:${timestamp}`;
}

function getToggleLoanPositionMessage(loanAddress: string, timestamp: number): string {
    return `${TOGGLE_LOAN_POSITION_MESSAGE_PREFIX}:${loanAddress}:${timestamp}`;
}

function getSetRefinanceConstraintMessage(
    loanAddress: string,
    minLiquidationThreshold: number,
    timestamp: number
): string {
    return `${SET_REFINANCE_CONSTRAINT_MESSAGE_PREFIX}:${loanAddress}:${minLiquidationThreshold}:${timestamp}`;
}

type ResolveAnyWallet = (params: ResolveAnyWalletParams) => Promise<Result<TransactionWalletAuth>>;

/** Shared: wallet auth → sign UTF-8 message → RTK mutation with timestamp + base64 signature. */
async function runWalletSignedMutation<TParams extends object>({
    resolveAnyWalletErrors,
    authDescription,
    params,
    buildMessage,
    mutate,
    failureMessage,
    wrapSignAndMutate
}: {
    resolveAnyWalletErrors: ResolveAnyWallet;
    authDescription: string;
    params: TParams;
    buildMessage: (params: TParams, timestamp: number) => string;
    mutate: (payload: TParams & { timestamp: number; signature: string }) => Promise<void>;
    failureMessage: string;
    wrapSignAndMutate?: (run: () => Promise<void>) => Promise<void>;
}): Promise<Result<void>> {
    const verifiedAuthRes = await resolveAnyWalletErrors({
        description: authDescription
    });
    if (!verifiedAuthRes.isOk()) return Result.err(verifiedAuthRes);

    const auth = verifiedAuthRes.unwrap();
    if (!("wallet" in auth)) {
        return Result.errFromMessage("Wallet connection required");
    }

    const { signMessage } = auth.wallet;
    if (!signMessage) {
        return Result.errFromMessage("Wallet does not support message signing");
    }

    const signAndMutate = async () => {
        const timestamp = getUnixTs();
        const message = new TextEncoder().encode(buildMessage(params, timestamp));
        const signatureBytes = await signMessage(message);
        const signature = base64.encode(Buffer.from(signatureBytes));
        await mutate({ ...params, timestamp, signature });
    };

    try {
        if (wrapSignAndMutate) {
            await wrapSignAndMutate(signAndMutate);
        } else {
            await signAndMutate();
        }
        return Result.ok(undefined);
    } catch (error) {
        return Result.errWithDebug(failureMessage, error);
    }
}

export function useGetUpdateVaultMetadata() {
    const [updateVaultMetadata] = useUpdateVaultMetadataWithSignatureMutation();
    const resolveAnyWalletErrors = useResolveAnyWalletErrors();

    return async (params: Omit<UpdateVaultMetadataParams, "signature" | "timestamp">): Promise<Result<void>> => {
        return runWalletSignedMutation({
            resolveAnyWalletErrors,
            authDescription: "Authenticate with your wallet to update vault metadata",
            params,
            buildMessage: (_, timestamp) => getUpdateVaultMetadataMessage(timestamp),
            mutate: (p) => updateVaultMetadata(p).unwrap(),
            failureMessage: "Failed to update vault metadata"
        });
    };
}

export function useGetToggleLoanPositionWithSignature() {
    const [toggleLoanPosition] = useToggleLoanPositionMutation();
    const resolveAnyWalletErrors = useResolveAnyWalletErrors();
    const { alert, dismiss } = useAlert();

    const toggleSignDescription = "Sign to confirm (no fee). This action is reversible.";

    return async (params: Omit<ToggleLoanPositionParams, "signature" | "timestamp">): Promise<Result<void>> => {
        return runWalletSignedMutation({
            resolveAnyWalletErrors,
            authDescription: toggleSignDescription,
            params,
            buildMessage: (p, timestamp) => getToggleLoanPositionMessage(p.loanAddress, timestamp),
            mutate: (p) => toggleLoanPosition(p).unwrap(),
            failureMessage: "Failed to toggle loan position",
            wrapSignAndMutate: async (run) => {
                const customSnackbarId = generateNonce();
                alert(COPY.WALLET_SIGN_PROMPT, "spinner", {
                    customSnackbarId,
                    description: toggleSignDescription
                });
                try {
                    await run();
                } finally {
                    dismiss(customSnackbarId);
                }
            }
        });
    };
}

export function useGetSetRefinanceConstraintWithSignature() {
    const [setRefinanceConstraint] = useSetRefinanceConstraintMutation();
    const resolveAnyWalletErrors = useResolveAnyWalletErrors();
    const { alert, dismiss } = useAlert();

    const signDescription = "Sign to set your refinance liquidation-threshold floor (no fee).";

    return async (params: Omit<SetRefinanceConstraintParams, "signature" | "timestamp">): Promise<Result<void>> => {
        return runWalletSignedMutation({
            resolveAnyWalletErrors,
            authDescription: signDescription,
            params,
            buildMessage: (p, timestamp) =>
                getSetRefinanceConstraintMessage(p.loanAddress, p.minLiquidationThreshold, timestamp),
            mutate: (p) => setRefinanceConstraint(p).unwrap(),
            failureMessage: "Failed to update refinance protection",
            wrapSignAndMutate: async (run) => {
                const customSnackbarId = generateNonce();
                alert(COPY.WALLET_SIGN_PROMPT, "spinner", {
                    customSnackbarId,
                    description: signDescription
                });
                try {
                    await run();
                } finally {
                    dismiss(customSnackbarId);
                }
            }
        });
    };
}

export interface TransactionDialogSenderOptions {
    description?: string;
    sendOptions?: TransactionSenderOptions;
    messageOverrides?: TransactionMessageOverrides;
    isMultiStep?: boolean;
    preventDispatch?: boolean;
    alerter?: AlerterOptions;
    mixpanelEventKey?: TrackTransactionEvent;
    mixpanelEvent?: { key: TrackTransactionEvent; params: TrackTransactionData<TrackTransactionEvent> };
    preventAutoDialogClose?: boolean;
}

function createSenderParams<ParamsType extends object>(
    transactionDetails: AbfTransactionDetails<ParamsType>,
    params: ParamsType | undefined,
    options?: TransactionDialogSenderOptions
): AbfMultiPartTransactionSenderParams {
    return {
        ...combineOptionsAndSender(transactionDetails, options),
        getTransactions: async (): AbfGeneratorResult =>
            transactionDetails.getTransactionsWithParams(params || ({} as ParamsType)),
        preventAutoDialogClose: options?.preventAutoDialogClose
    };
}

/**
 * Send transactions async
 * Handle wallet errors by forcing user to open dialog
 */
export function useTransactionSender() {
    const generateAndSend = useGenerateAndSendTransactions();
    const { setTransactionsLoading } = useTransactionsState();
    const preflightChecks = useTransactionPreflightChecks();
    const trackMixpanelEvent = useTrackFromTransactions();

    return async function send<ParamsType extends object>(
        transactionDetails: AbfTransactionDetails<ParamsType>,
        params: ParamsType | undefined,
        options?: TransactionDialogSenderOptions
    ): Promise<Result<TransactionResult[]>> {
        const senderParams = createSenderParams(transactionDetails, params, options);

        const preflightRes = await preflightChecks({ description: senderParams.description }, options);
        if (!preflightRes.isOk()) return Result.err(preflightRes);

        setTransactionsLoading(true);
        const res = await generateAndSend(preflightRes.unwrap(), senderParams);

        // Skip close of loader if there are more txns to send
        if (!options?.isMultiStep) {
            setTransactionsLoading(false);
        }

        if (options?.mixpanelEvent) {
            trackMixpanelEvent(res, options.mixpanelEvent);
        }

        return res;
    };
}

export function useTrackFromTransactions() {
    const { trackTransaction } = useTrack();

    return function trackMixpanelEvent(
        result: Result<TransactionResult[]>,
        { key, params }: NonNullable<TransactionDialogSenderOptions["mixpanelEvent"]>
    ) {
        if (!result.isOk()) {
            trackTransaction(key, {
                err: result.unwrapErrMessage()
            });
        } else if (!allTransactionsSucceeded(result)) {
            trackTransaction(key, {
                err: TX_FAILED_TO_CONFIRM,
                res: result.unwrap()
            });
        } else {
            trackTransaction(key, {
                params: params ?? undefined,
                res: result.unwrap()
            });
        }
    };
}

// verify wallet is setup correctly and account is set up
export function useTransactionPreflightChecks() {
    const resolveAnyWalletErrors = useResolveAnyWalletErrors();

    return async (
        params: ResolveAnyWalletParams,
        options?: TransactionDialogSenderOptions
    ): Promise<Result<TransactionWalletAuth>> => {
        const verifiedAuthRes = await resolveAnyWalletErrors(params);

        // Any wallet errors will intentionally silently fail
        if (!verifiedAuthRes.isOk()) return Result.err(verifiedAuthRes);

        return verifiedAuthRes;
    };
}

export function useGetSignedTransactionAuth() {
    const signIn = useSignInTransactionAuth();

    const resolveAnyWalletErrors = useResolveAnyWalletErrors();
    const { alert, dismiss } = useAlert();
    const state = useSelector(getSlice);
    return async (options?: { disableAlerts?: boolean; description?: string }): Promise<Result<string | undefined>> => {
        const verifiedAuthRes = await resolveAnyWalletErrors({
            description: options?.description ?? "Authenticate with your wallet"
        });
        // Any wallet errors will intentionally silently fail
        if (!verifiedAuthRes.isOk()) return Result.err(verifiedAuthRes);

        const auth = verifiedAuthRes.unwrap();

        // mpc doesn't need to sign
        if ("mpcIdentifier" in auth) {
            return Result.ok(undefined);
        }
        if (options?.disableAlerts) {
            return await signIn(auth, { authSlice: state });
        }

        const customSnackbarId = generateNonce();
        alert(COPY.WALLET_SIGN_PROMPT, "spinner", {
            customSnackbarId,
            description: options?.description
        });
        const res = await signIn(auth, { authSlice: state });
        dismiss(customSnackbarId);
        return res;
    };
}

// The Result fallback ("Something went wrong") and UNKNOWN_ERROR ("Something went wrong...")
// both start with the same prefix — treat either as "no useful message from the API".
function isGenericErrorMessage(message: string): boolean {
    return !message || message === UNKNOWN_ERROR || message.startsWith("Something went wrong");
}

function resolveTransactionGenerationError(
    transactionsRes: Result<unknown>,
    errorMessageMap: Record<string, string> | undefined
): string {
    const apiErrorMessage = transactionsRes.unwrapErrMessage();
    const mappedError = parseErrorFromOptions(apiErrorMessage, errorMessageMap ?? {});
    if (mappedError) return mappedError;
    // Prefer the API's own error message; fall back to the generic only when the
    // underlying error itself is generic (e.g. we lost the message upstream).
    return isGenericErrorMessage(apiErrorMessage) ? getReadableErrorMessage("generate transaction") : apiErrorMessage;
}

export function useGenerateAndSendTransactions() {
    const { alert, dismiss } = useAlert();
    const send = useSendTransactions();
    const { close: closeAppDialog } = useAppDialog();
    const { dispatchResults } = useTransactionsState();

    const getAndSign = useCallback(
        async (
            id: string,
            auth: TransactionWalletAuth,
            sender: AbfMultiPartTransactionSenderParams
        ): Promise<Result<TransactionResult[]>> => {
            const {
                description,
                sendOptions,
                getTransactions,
                messages: messageOverrides,
                preventDispatch,
                alerter,
                preventAutoDialogClose
            } = sender;

            const promptStep = (message: string, stepIndex: number, estimatedSeconds: number | undefined) =>
                alert(message, "spinner", {
                    description,
                    customSnackbarId: id,
                    progress:
                        !!alerter?.showProgress && estimatedSeconds !== undefined
                            ? { stepIndex, estimatedSeconds, totalSteps: alerter.totalSteps ?? 2 }
                            : undefined
                });

            const messages = getMessagesForTransactions(auth, messageOverrides);
            promptStep(messages.beforeGenerate, 0, alerter?.generationEstimateSeconds);

            const transactionsRes = await getTransactions();

            if (!transactionsRes.isOk()) {
                sendOptions.onFail?.();
                const parsedError = resolveTransactionGenerationError(
                    transactionsRes,
                    sender.messages?.errorMessageMap
                );
                return Result.errWithDebug(parsedError, transactionsRes);
            }

            // pause stepper until signature is complete
            promptStep(messages.beforeSignature, 0, 0);

            const { transactions: transactionsToSend } = transactionsRes.unwrap();

            if (getDebugWallet()) {
                await simulateTransactionsForDebugging(transactionsToSend, sendOptions);
                return Result.errFromMessage("Transaction aborted due to debug wallet. Logs in console");
            }
            const result = await send(auth, transactionsToSend, {
                ...sendOptions,
                onSign: () => {
                    sendOptions.onSign?.();
                    promptStep(messages?.beforeSend, 1, estimateTransactionLength(transactionsToSend));
                }
            });

            // return raw err since wallet errors are parsed
            if (!result.isOk()) {
                return Result.err(result);
            }

            const transactionResults = result.unwrapOr([]);
            if (!transactionResults.length) {
                return Result.err(messages.onError);
            }

            const timedOut = !!transactionResults.find((t) => t.error?.includes("Timeout"));
            const knownTransactionError = parseTransactionError(transactionResults);
            const onErrorMessage = knownTransactionError ?? messages.onError;

            if (!allTransactionsSucceeded(result)) {
                const wallet = getWalletFromAuth(auth);
                captureFailedTransactions(transactionResults, { description, onErrorMessage, timedOut, wallet });
            }

            if (!preventDispatch) {
                const resultsWithMissing = addMissingResults(transactionsToSend, transactionResults);

                dispatchResults(id, resultsWithMissing, {
                    onSuccess: messages.onSuccess,
                    onError: timedOut ? "Transaction timed out. Please try again" : onErrorMessage
                });
            }

            if (allTransactionsSucceeded(result) && !preventAutoDialogClose) {
                closeAppDialog();
            }

            return result;
        },
        [alert, closeAppDialog, dispatchResults, send]
    );

    // catches runtime errors not associated with transactions confirmation fails
    return async function (auth: TransactionWalletAuth, sender: AbfMultiPartTransactionSenderParams) {
        const id = sender.alerter?.customSnackbarId ?? generateNonce();
        const res = await getAndSign(id, auth, sender);

        // early dismiss the transaction spinner snackbar
        if (!res.isOk()) {
            dismiss(id);
        }

        return res;
    };
}

type AllMessages = {
    beforeSignature: string;
    beforeSend: string;
    beforeGenerate: string;
    onSuccess: string;
    onError: string;
};

function parseTransactionError(results: TransactionResult[]) {
    const failedTransactions = results.filter((r) => r.status !== TransactionStatus.Confirmed);
    const staleOracleMessage = failedTransactions
        .map(getStaleOracleSimulationMessageFromTransactionResult)
        .find((message): message is string => !!message);
    if (staleOracleMessage) return staleOracleMessage;

    const firstError = failedTransactions.find((r) => !!r.error);
    if (!firstError?.error) return;

    // Capture the custom error code: e.g. "Custom(6001)"
    const match = /Custom\((\d+)\)/.exec(firstError.error);
    if (!match?.length) return;

    const code = match[1];

    const knownErrorMap: Record<string, string> = {
        [CustomContractErrors.INSUFFICIENT_COLLATERAL]:
            "Insufficient collateral. Please reduce the borrow amount and try again."
    };

    return knownErrorMap[code];
}

function captureFailedTransactions(
    transactionResults: TransactionResult[],
    context: { description?: string; onErrorMessage?: string; timedOut?: boolean; wallet?: string }
) {
    const failedTransactions = transactionResults.filter((r) => r.status !== TransactionStatus.Confirmed);
    if (!failedTransactions.length) return;

    Sentry.captureMessage("Transaction failed", {
        level: "error",
        extra: {
            description: context.description,
            onErrorMessage: context.onErrorMessage,
            timedOut: context.timedOut,
            wallet: context.wallet,
            failedTransactions: failedTransactions.map((r) => {
                const statusLabel = typeof r.status === "number" ? TransactionStatus[r.status] ?? r.status : r.status;
                return {
                    identifier: r.identifier,
                    signature: r.signature,
                    status: statusLabel,
                    error: r.error,
                    simulationLogs: r.simulationLogs
                };
            })
        }
    });
}

export function getMessagesForTransactions(
    auth: TransactionWalletAuth,
    messageOverrides: TransactionMessageOverrides | undefined
) {
    const isMpc = "mpcIdentifier" in auth;
    const beforeSignature = isMpc ? COPY.PASSKEY_SIGN_PROMPT : COPY.WALLET_SIGN_PROMPT;
    return parseMessageOverrides(messageOverrides, {
        beforeSend: "Sending transactions",
        onSuccess: "All changes confirmed",
        onError: "Transactions failed to confirm",
        beforeGenerate: "Generating transactions",
        beforeSignature
    });
}

function getWalletFromAuth(auth: TransactionWalletAuth): string | undefined {
    if ("wallet" in auth) return auth.wallet.publicKey.toBase58();
    return auth.mpcIdentifier;
}

function parseMessageOverrides(overrides: TransactionMessageOverrides | undefined, fallback: AllMessages): AllMessages {
    if (!overrides) return fallback;

    return {
        beforeGenerate: overrides.beforeGenerate ?? overrides.loadingMessage ?? fallback.beforeGenerate,
        beforeSend: overrides.beforeSend ?? overrides.loadingMessage ?? fallback.beforeSend,
        beforeSignature: overrides.beforeSignature ?? overrides.loadingMessage ?? fallback.beforeSignature,
        onError: overrides.onError ?? fallback.onError,
        onSuccess: overrides.onSuccess ?? fallback.onSuccess
    };
}

/**
 * Opens the transaction dialog to handle wallet errors
 * This check catches MPC device not found, device not registered, unconnected self custodial wallet, etc
 * Open dialog promise resolve/reject is passed through redux to dialog directly.
 * Passing callbacks to redux store is slightly bad practice but is necessary for compensability with other dialogs
 * Returns validated auth (MPC or wallet) which has been validated by the async connect dialog
 */
function useResolveAnyWalletErrors() {
    const { open } = useAppDialog();

    // Pass auth in a callback so that it is the updated, not cached from when hook was called
    const openConnect = (params: ResolveAnyWalletParams) =>
        new Promise<TransactionWalletAuth>((resolve, reject) => {
            open(
                AppDialog.TransactionSender,
                {
                    ...params,
                    resolve,
                    reject
                },
                true
            );
        });

    return async (params: ResolveAnyWalletParams): Promise<Result<TransactionWalletAuth>> => {
        try {
            const auth = await openConnect(params);
            return Result.ok(auth);
        } catch (error) {
            return Result.errFromMessage("Transaction cancelled", { skipSentry: true });
        }
    };
}

export function combineOptionsAndSender<ParamsType extends object>(
    transactionDetails: AbfTransactionDetails<ParamsType>,
    options?: TransactionDialogSenderOptions
): BaseAbfMultiPartTransactionSender {
    return {
        description: options?.description ?? transactionDetails.description,
        sendOptions: { ...transactionDetails.sendOptions, ...options?.sendOptions },
        messages: { ...transactionDetails.messages, ...options?.messageOverrides },
        preventDispatch: options?.preventDispatch,
        alerter: options?.alerter
    };
}

export function allTransactionsSucceeded(results: Result<TransactionResult[]>) {
    if (!results.isOk()) return false;
    return !results.unwrap().find((r) => r.error || r.status !== TransactionStatus.Confirmed);
}

function addMissingResults(transactionsToSend: OrderedTransactions, transactionResults: TransactionResult[]) {
    const resultsCopy = [...transactionResults];
    const foundIdentifierResults = new Set(transactionResults.map((r) => r.identifier));

    // first see if any unique identifiers were dropped
    const missingIdentifiers = transactionsToSend
        .map((txn) => txn.transactions)
        .flat()
        .filter((txn) => !foundIdentifierResults.has(txn.identifier));

    if (missingIdentifiers.length) {
        resultsCopy.push(
            ...missingIdentifiers.map(
                ({ identifier }): TransactionResult => ({
                    signature: "",
                    status: TransactionStatus.Error,
                    identifier
                })
            )
        );
    }

    // if there are still missing transactions, fill in as unknowns
    const missingResponses = transactionsToSend.length - resultsCopy.length;
    if (missingResponses > 0) {
        resultsCopy.push(
            ...Array<TransactionResult>(missingResponses).fill({
                signature: "",
                status: TransactionStatus.Error,
                identifier: "Unknown"
            })
        );
    }

    return resultsCopy;
}

export function estimateTransactionLength(transactionsToSend: OrderedTransactions) {
    let estimateSeconds = 0;
    for (const { transactions, commitmentLevel } of transactionsToSend) {
        if (commitmentLevel === "finalized") estimateSeconds += 40;
        if (commitmentLevel === "confirmed") estimateSeconds += 20;
        estimateSeconds += transactions.length;
    }
    return estimateSeconds;
}

async function simulateTransactionsForDebugging(
    orderedTransactions: OrderedTransactions,
    sendOptions?: TransactionSenderOptions
) {
    // only simulate for debug wallets
    const debugWallet = getDebugWallet();
    if (!debugWallet) return Result.ok();

    const connection = new Connection(RPC_URL);
    let hasTransactionError = false;

    for (const [batchIndex, { transactions }] of orderedTransactions.entries()) {
        const shouldUseBundleSim = sendOptions?.bundle === "always";
        if (shouldUseBundleSim) {
            const encodedTransactions: string[] = [];
            let bundleEncodingFailed = false;

            for (const [transactionIndex, { transaction, identifier }] of transactions.entries()) {
                if (isVersionedTransaction(transaction)) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `debug transaction info, ${formatSimulationTransactionLabel(batchIndex, transactionIndex, identifier)}`
                    );
                    await logTransactionDebugInfo(transaction, connection);
                }

                const encodedTransaction = trySerializeAndEncodeTransaction(transaction);
                if (encodedTransaction.isErr()) {
                    bundleEncodingFailed = true;
                    hasTransactionError = true;
                    // eslint-disable-next-line no-console
                    console.log(
                        `debug bundle simulation error, ${formatSimulationTransactionLabel(batchIndex, transactionIndex, identifier)}:`,
                        encodedTransaction.unwrapErrMessage()
                    );
                    if (isVersionedTransaction(transaction)) {
                        await logTransactionDebugInfo(transaction, connection);
                    }
                    break;
                }
                encodedTransactions.push(encodedTransaction.unwrap());
            }

            if (bundleEncodingFailed) continue;

            const bundleSimResult = await simulateBundleForDebugging(encodedTransactions);

            if (!bundleSimResult.isOk() || bundleSimResult.unwrapOr(false)) {
                hasTransactionError = true;
                await simulateBatchTransactionsIndividuallyForDebugging(
                    transactions,
                    connection,
                    debugWallet,
                    batchIndex,
                    bundleSimResult.isErr() ? bundleSimResult.unwrapErrMessage() : undefined
                );
            }
            continue;
        }

        for (const { transaction } of transactions) {
            let res;
            try {
                res = await simulateTransaction(connection, transaction, [debugWallet]);
                if (res.value?.err) {
                    hasTransactionError = true;
                }
                // eslint-disable-next-line no-console
                console.log(res);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log("debug error: ", error);
                hasTransactionError = true;
            }

            if (hasTransactionError && transaction instanceof VersionedTransaction) {
                await logTransactionDebugInfo(transaction, connection);
            }
        }
    }

    return Result.ok();
}

async function simulateBatchTransactionsIndividuallyForDebugging(
    transactions: OrderedTransactions[number]["transactions"],
    connection: Connection,
    debugWallet: string,
    batchIndex: number,
    bundleError?: string
) {
    // eslint-disable-next-line no-console
    console.log("debug bundle simulation failed; running individual simulations", {
        batchIndex,
        bundleError
    });

    for (const [transactionIndex, { transaction, identifier }] of transactions.entries()) {
        const transactionLabel = formatSimulationTransactionLabel(batchIndex, transactionIndex, identifier);
        try {
            const res = await simulateTransaction(connection, transaction, [debugWallet]);
            // eslint-disable-next-line no-console
            console.log(`debug individual simulation, ${transactionLabel}`, res);
            if (res.value?.err && isVersionedTransaction(transaction)) {
                await logTransactionDebugInfo(transaction, connection);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`debug individual simulation error, ${transactionLabel}:`, error);
            if (isVersionedTransaction(transaction)) {
                await logTransactionDebugInfo(transaction, connection);
            }
        }
    }
}

function formatSimulationTransactionLabel(batchIndex: number, transactionIndex: number, identifier?: string) {
    const label = `batch ${batchIndex + 1}, transaction ${transactionIndex + 1}`;
    return identifier ? `${label} (${identifier})` : label;
}

async function simulateBundleForDebugging(encodedTransactions: string[]): Promise<Result<boolean>> {
    const result = await simulateBundle(encodedTransactions);
    if (result.isErr()) {
        return Result.err(result);
    }

    return Result.ok(false);
}

export function useTransactionSettings() {
    const initialSettings: TransactionSettingsHeader = {
        maxFee: undefined,
        exactFee: undefined,
        feeLevel: undefined,
        broadcastMode: undefined
    };

    const [txnSettings, setTxnSettings] = useLocalStorage<TransactionSettingsHeader>(
        TRANSACTION_SETTINGS_COOKIE_PREFIX,
        initialSettings
    );

    const convertTxSettingsToLamports = (settings: TransactionSettingsHeader) => ({
        ...settings,
        maxFee: settings.maxFee ? uiAmountToLamports(settings.maxFee, SOL_DECIMALS) : undefined,
        exactFee: settings.exactFee ? uiAmountToLamports(settings.exactFee, SOL_DECIMALS) : undefined
    });

    const compareUnsavedChangesInLamports = (current: TransactionSettingsHeader, saved: TransactionSettingsHeader) => {
        return (
            current.maxFee !== saved.maxFee ||
            current.exactFee !== saved.exactFee ||
            current.feeLevel !== saved.feeLevel ||
            current.broadcastMode !== saved.broadcastMode
        );
    };
    return {
        txnSettings,
        setTxnSettings,
        compareUnsavedChangesInLamports,
        convertTxSettingsToLamports
    };
}

/**
 * Sign-and-send counterpart to useGenerateAndSendTransactions.
 * Same (auth, senderParams) contract; uses wallet sign-and-send instead of backend pipeline.
 */
export function useGenerateSignAndSendTransactions() {
    const signAndSend = useSignAndSendTransactions();
    const { dispatchResults } = useTransactionsState();
    const { close: closeAppDialog } = useAppDialog();

    return async function (
        auth: TransactionWalletAuth,
        sender: AbfMultiPartTransactionSenderParams
    ): Promise<Result<TransactionResult[]>> {
        const {
            sendOptions,
            getTransactions,
            messages: messageOverrides,
            preventDispatch,
            preventAutoDialogClose
        } = sender;

        const transactionsRes = await getTransactions();
        if (!transactionsRes.isOk()) {
            sendOptions?.onFail?.();
            const parsedError = resolveTransactionGenerationError(transactionsRes, sender.messages?.errorMessageMap);
            return Result.errWithDebug(parsedError, transactionsRes);
        }

        const { transactions: transactionsToSend } = transactionsRes.unwrap();
        const messages = getMessagesForTransactions(auth, messageOverrides);

        const result = await signAndSend(auth, transactionsToSend, sendOptions ?? {});

        if (!result.isOk()) return Result.err(result);

        const transactionResults = result.unwrapOr([]);
        if (!transactionResults.length) return Result.err(messages.onError);

        if (!preventDispatch) {
            const resultsWithMissing = addMissingResults(transactionsToSend, transactionResults);
            dispatchResults(generateNonce(), resultsWithMissing, {
                onSuccess: messages.onSuccess,
                onError: messages.onError
            });
        }
        if (allTransactionsSucceeded(result) && !preventAutoDialogClose) {
            closeAppDialog();
        }
        return result;
    };
}

/**
 * Same flow as useTransactionSender but uses wallet sign-and-send (e.g. for swap).
 */
export function useSignAndSendTransactionSender() {
    const generateSignAndSend = useGenerateSignAndSendTransactions();
    const { setTransactionsLoading } = useTransactionsState();
    const preflightChecks = useTransactionPreflightChecks();
    const trackMixpanelEvent = useTrackFromTransactions();

    return async function send<ParamsType extends object>(
        transactionDetails: AbfTransactionDetails<ParamsType>,
        params: ParamsType | undefined,
        options?: TransactionDialogSenderOptions
    ): Promise<Result<TransactionResult[]>> {
        const senderParams = createSenderParams(transactionDetails, params, options);

        const preflightRes = await preflightChecks({ description: senderParams.description }, options);
        if (!preflightRes.isOk()) return Result.err(preflightRes);

        setTransactionsLoading(true);
        const res = await generateSignAndSend(preflightRes.unwrap(), senderParams);

        if (!options?.isMultiStep) {
            setTransactionsLoading(false);
        }

        if (options?.mixpanelEvent) {
            trackMixpanelEvent(res, options.mixpanelEvent);
        }

        return res;
    };
}
